import React from 'react';
import { Download, Github, Shield, Database, Network, Lock, Server, Brain, Mail } from 'lucide-react';

const ANSLanding = () => {
  // GitHub link for the repository
  const GITHUB_LINK = "https://github.com/g-clouds/ans";
  
  // Whitepaper site link
  const WHITEPAPER_SITE_LINK = "https://g-clouds.github.io/ANS/";

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Hero Section */}
      <section className="relative pt-24 pb-32 px-6 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-slate-950 pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-blue-500/10 rounded-full px-4 py-2 mb-8">
                <Shield className="w-5 h-5 text-blue-400" />
                <span className="text-blue-400">gClouds R&D | gLabs</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Agent Network{' '}
              <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
                System
              </span>
              </h1>
              <p className="text-xl text-slate-300 mb-8">
              A foundational hybrid architecture for secure AI agent ecosystems with high performance discovery, multi-level trust, and confidential verification.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href={WHITEPAPER_SITE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg flex items-center justify-center space-x-2 transition-colors"
              >
                <Download className="w-5 h-5" />
                <span>View Whitepaper</span>
              </a>
              <a 
                href={GITHUB_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-slate-800 hover:bg-slate-700 text-white px-8 py-4 rounded-lg flex items-center justify-center space-x-2 transition-colors"
              >
                <Github className="w-5 h-5" />
                <span>View on GitHub</span>
              </a>
              </div>
            </div>
            
            <div className="relative">
            <div className="relative bg-slate-800/50 backdrop-blur-sm rounded-xl overflow-hidden shadow-xl border border-slate-700/50 max-w-5xl mx-auto">
                {/* YouTube Video Embed */}
                <iframe
                  className="w-full aspect-video"
                  src="https://www.youtube.com/embed/Axq2oglXtwc?autoplay=1&modestbranding=1&rel=0&showinfo=0"
                  title="Agent Newtwork System Intro"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl"></div>
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-purple-500/20 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
        </section>

      {/* Overview Section */}
      <section className="py-20 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">What is ANS?</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              The Agent Network System provides a unified framework for AI agents to discover, verify, and connect with each other securely across organisational boundaries.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Network />}
              title="Global Discovery"
              description="A unified system for agent lookup by name or capability, ensuring seamless integration across platforms."
            />
            <FeatureCard
              icon={<Lock />}
              title="Multi-Level Trust"
              description="Progressive verification from basic signatures to blockchain-based validation, balancing performance with security."
            />
            <FeatureCard
              icon={<Shield />}
              title="Zero-Trust Security"
              description="Continuous verification at all system levels with no implicit trust between components or participants."
            />
          </div>
        </div>
      </section>

      {/* Architecture Section with Diagram */}
      <section className="py-20 bg-slate-900/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Hybrid Architecture</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              ANS combines centralised components for performance with decentralised systems for trust, creating an architecture that delivers the best of both worlds.
            </p>
          </div>

          {/* Architecture Diagram */}
          <div className="mb-16 flex justify-center">
            <div className="bg-slate-800/30 backdrop-blur-xl rounded-xl p-3 md:p-6 border border-white/10 w-full max-w-6xl overflow-hidden">
              <svg viewBox="0 0 800 500" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto min-h-80 md:min-h-96">
                {/* Background */}
                <defs>
                  <linearGradient id="bgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.05" />
                    <stop offset="50%" stopColor="#8b5cf6" stopOpacity="0.05" />
                    <stop offset="100%" stopColor="#0f172a" stopOpacity="0.1" />
                  </linearGradient>
                </defs>
                <rect width="800" height="500" fill="url(#bgGradient)" rx="15" />
                
                {/* Layers */}
                {/* Centralized Layer */}
                <rect x="100" y="80" width="600" height="100" rx="8" fill="#334155" fillOpacity="0.6" stroke="#60a5fa" strokeWidth="2" />
                <text x="150" y="110" fontFamily="Arial" fontSize="18" fill="#ffffff" fontWeight="bold">Centralised High Performance Layer</text>
                <text x="150" y="135" fontFamily="Arial" fontSize="13" fill="#94a3b8">Fast lookups, high scalability, global caching</text>
                <rect x="150" y="150" width="100" height="25" rx="5" fill="#1e293b" stroke="#60a5fa" strokeWidth="1" />
                <text x="200" y="167" fontFamily="Arial" fontSize="11" fill="#ffffff" textAnchor="middle">Registry Gateway</text>
                <rect x="260" y="150" width="118" height="25" rx="5" fill="#1e293b" stroke="#60a5fa" strokeWidth="1" />
                <text x="320" y="167" fontFamily="Arial" fontSize="11" fill="#ffffff" textAnchor="middle">High-Performance DB</text>
                <rect x="390" y="150" width="100" height="25" rx="5" fill="#1e293b" stroke="#60a5fa" strokeWidth="1" />
                <text x="440" y="167" fontFamily="Arial" fontSize="11" fill="#ffffff" textAnchor="middle">Global Cache</text>
                <rect x="510" y="150" width="100" height="25" rx="5" fill="#1e293b" stroke="#60a5fa" strokeWidth="1" />
                <text x="560" y="167" fontFamily="Arial" fontSize="11" fill="#ffffff" textAnchor="middle">Authentication</text>

                {/* Bridge Layer */}
                <rect x="100" y="200" width="600" height="100" rx="8" fill="#334155" fillOpacity="0.6" stroke="#a78bfa" strokeWidth="2" />
                <text x="150" y="230" fontFamily="Arial" fontSize="18" fill="#ffffff" fontWeight="bold">Hybrid Bridge Layer</text>
                <text x="150" y="255" fontFamily="Arial" fontSize="13" fill="#94a3b8">Synchronisation, trust protocols, policy distribution</text>
                <rect x="150" y="270" width="135" height="25" rx="5" fill="#1e293b" stroke="#a78bfa" strokeWidth="1" />
                <text x="217" y="287" fontFamily="Arial" fontSize="11" fill="#ffffff" textAnchor="middle">Synchronisation Engine</text>
                <rect x="305" y="270" width="135" height="25" rx="5" fill="#1e293b" stroke="#a78bfa" strokeWidth="1" />
                <text x="372" y="287" fontFamily="Arial" fontSize="11" fill="#ffffff" textAnchor="middle">Trust Verification Protocol</text>
                <rect x="460" y="270" width="135" height="25" rx="5" fill="#1e293b" stroke="#a78bfa" strokeWidth="1" />
                <text x="527" y="287" fontFamily="Arial" fontSize="11" fill="#ffffff" textAnchor="middle">Policy Distribution</text>

                {/* Decentralized Layer */}
                <rect x="100" y="320" width="600" height="100" rx="8" fill="#334155" fillOpacity="0.6" stroke="#f472b6" strokeWidth="2" />
                <text x="150" y="350" fontFamily="Arial" fontSize="18" fill="#ffffff" fontWeight="bold">Decentralised Trust Layer</text>
                <text x="150" y="375" fontFamily="Arial" fontSize="13" fill="#94a3b8">Blockchain-based trust guarantees, distributed validation</text>
                <rect x="150" y="390" width="110" height="25" rx="5" fill="#1e293b" stroke="#f472b6" strokeWidth="1" />
                <text x="205" y="407" fontFamily="Arial" fontSize="11" fill="#ffffff" textAnchor="middle">Blockchain Registry</text>
                <rect x="280" y="390" width="110" height="25" rx="5" fill="#1e293b" stroke="#f472b6" strokeWidth="1" />
                <text x="335" y="407" fontFamily="Arial" fontSize="11" fill="#ffffff" textAnchor="middle">Distributed Validators</text>
                <rect x="410" y="390" width="110" height="25" rx="5" fill="#1e293b" stroke="#f472b6" strokeWidth="1" />
                <text x="465" y="407" fontFamily="Arial" fontSize="11" fill="#ffffff" textAnchor="middle">Smart Contracts</text>
                <rect x="540" y="390" width="110" height="25" rx="5" fill="#1e293b" stroke="#f472b6" strokeWidth="1" />
                <text x="595" y="407" fontFamily="Arial" fontSize="11" fill="#ffffff" textAnchor="middle">ZK Proofs</text>

                {/* Connections between layers */}
                <line x1="300" y1="180" x2="300" y2="200" stroke="#a78bfa" strokeWidth="3" strokeDasharray="5,5" />
                <line x1="400" y1="180" x2="400" y2="200" stroke="#a78bfa" strokeWidth="3" strokeDasharray="5,5" />
                <line x1="500" y1="180" x2="500" y2="200" stroke="#a78bfa" strokeWidth="3" strokeDasharray="5,5" />
                
                <line x1="200" y1="295" x2="200" y2="320" stroke="#f472b6" strokeWidth="3" strokeDasharray="5,5" />
                <line x1="370" y1="295" x2="370" y2="320" stroke="#f472b6" strokeWidth="3" strokeDasharray="5,5" />
                <line x1="550" y1="295" x2="550" y2="320" stroke="#f472b6" strokeWidth="3" strokeDasharray="5,5" />

                {/* Operations */}
                {/* User/Agent */}
                <circle cx="410" cy="30" r="25" fill="#0f172a" stroke="#60a5fa" strokeWidth="2" />
                <text x="410" y="35" fontFamily="Arial" fontSize="14" fill="#ffffff" textAnchor="middle">Agent</text>
                
                {/* Operation Arrows */}
                <path d="M 365 45 L 335 80" stroke="#60a5fa" strokeWidth="2" markerEnd="url(#arrowhead)" />
                <text x="335" y="60" fontFamily="Arial" fontSize="12" fill="#ffffff" textAnchor="middle">Lookup</text>
                
                <path d="M 400 55 L 400 80" stroke="#60a5fa" strokeWidth="2" markerEnd="url(#arrowhead)" />
                <text x="400" y="70" fontFamily="Arial" fontSize="12" fill="#ffffff" textAnchor="middle">Register</text>
                
                <path d="M 435 45 L 465 80" stroke="#60a5fa" strokeWidth="2" markerEnd="url(#arrowhead)" />
                <text x="465" y="65" fontFamily="Arial" fontSize="12" fill="#ffffff" textAnchor="middle">Verify</text>
                
                <path d="M 465 45 L 525 80" stroke="#60a5fa" strokeWidth="2" markerEnd="url(#arrowhead)" />
                <text x="525" y="65" fontFamily="Arial" fontSize="11" fill="#ffffff" textAnchor="middle">Policies</text>
                
                {/* Arrowhead marker */}
                <defs>
                  <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="8.5" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#60a5fa" />
                  </marker>
                </defs>
                
                {/* Title */}
                <text x="400" y="470" fontFamily="Arial" fontSize="16" fill="#ffffff" textAnchor="middle" fontWeight="bold">Agent Network System (ANS) Architecture</text>
              </svg>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ArchitectureCard
              icon={<Database />}
              title="Centralised High Performance Layer"
              description="Optimised for speed and scalability, delivers <50ms p95 latency via global caching and edge nodes"
              features={["Registry Gateway", "High Performance Database", "Global Cache Layer", "Authentication Service"]}
            />
            <ArchitectureCard
              icon={<Server />}
              title="Hybrid Bridge Layer"
              description="Bridges centralised and decentralised components, ensuring consistency, trust, and policy enforcement."
              features={["Synchronisation Engine", "Conflict Resolution", "Trust Verification Protocol", "Policy Distribution"]}
            />
            <ArchitectureCard
              icon={<Brain />}
              title="Decentralised Trust Layer"
              description="Provides strong trust guarantees through blockchain technology and distributed validation."
              features={["Blockchain Registry", "Distributed Validators", "Smart Contracts", "Zero-Knowledge Proofs"]}
            />
          </div>
        </div>
      </section>

      {/* Open Community Section */}
      <section className="py-20 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">An Open Community Initiative</h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              ANS is designed as an open standard, developed collaboratively with the global AI community to ensure interoperability, security, and trust across agent ecosystems.
            </p>
          </div>

          <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-8 border border-white/10">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-semibold mb-4">Join the Community</h3>
              <p className="text-gray-300 max-w-3xl mx-auto mb-6">
                We believe that agent interoperability requires an open, collaborative approach. ANS is built on principles of:
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
              <div className="bg-slate-900/40 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto bg-blue-500/10 rounded-lg flex items-center justify-center mb-4 text-blue-400">
                  <Github className="w-6 h-6" />
                </div>
                <h4 className="text-lg font-medium mb-2">Open Source</h4>
                <p className="text-gray-400">
                  All ANS specifications and reference implementations are available as open source resources for the entire community.
                </p>
              </div>
              
              <div className="bg-slate-900/40 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto bg-blue-500/10 rounded-lg flex items-center justify-center mb-4 text-blue-400">
                  <Network className="w-6 h-6" />
                </div>
                <h4 className="text-lg font-medium mb-2">Collaborative Governance</h4>
                <p className="text-gray-400">
                  Development decisions are made transparently with input from participants across the AI ecosystem.
                </p>
              </div>
              
              <div className="bg-slate-900/40 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto bg-blue-500/10 rounded-lg flex items-center justify-center mb-4 text-blue-400">
                  <Shield className="w-6 h-6" />
                </div>
                <h4 className="text-lg font-medium mb-2">Vendor-Neutral</h4>
                <p className="text-gray-400">
                  Designed to be platform-agnostic, ensuring no single entity controls the agent ecosystem.
                </p>
              </div>
            </div>
            
            <div className="text-center">
              <p className="text-gray-300 mb-8">
                The specification is available in Markdown format on GitHub, making it easy to contribute, suggest improvements, and adapt for your implementation.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <a 
                  href={GITHUB_LINK}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-slate-800 hover:bg-slate-700 text-white px-8 py-3 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                >
                  <Github className="w-5 h-5" />
                  <span>View Specification on GitHub</span>
                </a>
                <a 
                  href={WHITEPAPER_SITE_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                >
                  <Download className="w-5 h-5" />
                  <span>View Whitepaper</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl p-12 text-center backdrop-blur-sm border border-white/10">
            <h2 className="text-3xl font-bold mb-4">Contribute to ANS</h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              ANS is offered as a foundation for industry collaboration. Join us in developing this specification to meet the needs of a diverse and rapidly growing agent ecosystem.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
              <a 
                href={GITHUB_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-slate-800 hover:bg-slate-700 text-white px-8 py-4 rounded-lg flex items-center justify-center space-x-2 transition-colors"
              >
                <Github className="w-5 h-5" />
                <span>Star on GitHub</span>
              </a>
              <a 
                href={`${GITHUB_LINK}/issues/new`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-slate-800 hover:bg-slate-700 text-white px-8 py-4 rounded-lg flex items-center justify-center space-x-2 transition-colors"
              >
                <Shield className="w-5 h-5" />
                <span>Submit Feedback</span>
              </a>
                <button
                  onClick={() => window.location.href = 'mailto:ans@gclouds.co.uk'}
                  className="bg-slate-800 hover:bg-slate-700 text-white px-8 py-4 rounded-lg flex items-center justify-center space-x-2 transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  <span>Contact gLabs team</span>
                </button>
            </div>
            <p className="text-gray-400 text-sm">
              Together, we can build a more interconnected, secure, and trustworthy agent ecosystem.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

// Feature Card Component
const FeatureCard = ({ icon, title, description }) => (
  <div className="bg-slate-900/50 backdrop-blur-xl rounded-xl p-8 border border-white/10 hover:border-blue-500/50 transition-colors">
    <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-6 text-blue-400">
      {icon}
    </div>
    <h3 className="text-xl font-semibold mb-4">{title}</h3>
    <p className="text-gray-400">{description}</p>
  </div>
);

// Architecture Card Component
const ArchitectureCard = ({ icon, title, description, features }) => (
  <div className="bg-slate-900/50 backdrop-blur-xl rounded-xl p-8 border border-white/10 hover:border-blue-500/50 transition-colors">
    <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-6 text-blue-400">
      {icon}
    </div>
    <h3 className="text-xl font-semibold mb-4">{title}</h3>
    <p className="text-gray-400 mb-6">{description}</p>
    <ul className="space-y-3">
      {features.map((feature, index) => (
        <li key={index} className="flex items-start space-x-2 text-gray-300">
          <Shield className="w-5 h-5 text-blue-400 mt-1" />
          <span>{feature}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default ANSLanding;